import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API } from "../../../contexts/AuthContext";
import { DaySelector } from "./DaySelector";
import { DaySection } from "./DaySection";
import { RoadmapPDFButton } from "./RoadmapPDFButton";
import { LockedDays } from "../LockedDays";
import { toast } from "sonner";
import { RoadmapContinue } from "./RoadmapContinue";

const LORA  = { fontFamily: "'Lora', Georgia, serif" };
const SERIF = { fontFamily: "'Cormorant Garamond', Georgia, serif" };

function SkeletonRoadmap() {
  return (
    <div className="animate-pulse space-y-10 mt-6">
      {[1, 2, 3].map((i) => (
        <div key={i}>
          <div className="h-7 bg-[#EDE8E0] rounded w-44 mb-1" />
          <div className="h-4 bg-[#EDE8E0] rounded w-56 mb-5" />
          {[1, 2, 3].map((j) => (
            <div key={j} className="flex gap-3 mb-5">
              <div className="w-9 h-9 rounded-full bg-[#EDE8E0] flex-shrink-0" />
              <div className="flex-1 space-y-2 pt-1">
                <div className="h-4 bg-[#EDE8E0] rounded w-3/4" />
                <div className="h-3 bg-[#EDE8E0] rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export function RoadmapTab({ tripId, trip, onUnlock }) {
  const [roadmap, setRoadmap] = useState(null);
  const [status, setStatus]   = useState("loading");
  const [activeDay, setActiveDay] = useState(0);
  const dayRefs  = useRef([]);
  const pollRef  = useRef(null);
  const errorShownRef = useRef(false);
  const today    = new Date().toISOString().split("T")[0];

  const fetchRoadmap = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/trips/${tripId}/roadmap`);
      if (data.offline) return;
      setStatus(data.status);
      if (data.status === "completed" && data.jours?.length > 0) {
        setRoadmap(data);
        const todayIdx = data.jours.findIndex((j) => j.date === today);
        if (todayIdx !== -1) setActiveDay((prev) => (prev === 0 ? todayIdx : prev));
      }
    } catch {
      // polling: warn once, keep retrying silently afterwards
      if (!errorShownRef.current) {
        errorShownRef.current = true;
        toast.error("Impossible de charger la feuille de route. Vérifiez votre connexion.", { id: "roadmap-load-error" });
      }
    }
  }, [tripId, today]);

  // Initial fetch
  useEffect(() => { fetchRoadmap(); }, [fetchRoadmap]);

  // Poll every 5s while still pending/loading
  useEffect(() => {
    if (status === "pending" || status === "loading" || roadmap?.extending) {
      pollRef.current = setInterval(fetchRoadmap, 5000);
    } else {
      clearInterval(pollRef.current);
    }
    return () => clearInterval(pollRef.current);
  }, [status, fetchRoadmap, roadmap?.extending]);

  const scrollToDay = (idx) => {
    setActiveDay(idx);
    dayRefs.current[idx]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Compute nb_jours for subtitle
  let nbJours = 0;
  try {
    const d1 = new Date(trip.date_depart);
    const d2 = new Date(trip.date_retour);
    nbJours = Math.max(1, Math.round((d2 - d1) / 86400000) + 1);
  } catch {}

  return (
    <div className="pb-16" data-testid="roadmap-tab">
      {/* Section header */}
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h2 className="text-3xl sm:text-4xl font-semibold text-[#2B2623]" style={SERIF}>
            Feuille de Route
          </h2>
          <p className="text-sm text-[#9A8F86] mt-1 italic" style={LORA}>
            {nbJours} jour{nbJours > 1 ? "s" : ""} · {trip.destination}
          </p>
        </div>
        {status === "completed" && roadmap?.jours?.length > 0 && !roadmap.trial_locked && (
          <div className="flex-shrink-0 mt-1">
            <RoadmapPDFButton roadmap={roadmap} trip={trip} />
          </div>
        )}
      </div>

      {/* Pending / loading state */}
      {(status === "loading" || status === "pending") && (
        <>
          <div
            className="flex items-center gap-2 text-sm text-[#9A8F86] italic"
            style={LORA}
            data-testid="roadmap-loading"
          >
            <div className="w-3 h-3 border-2 border-[#C17F59] border-t-transparent rounded-full animate-spin flex-shrink-0" />
            Votre feuille de route est en cours de préparation...
          </div>
          <SkeletonRoadmap />
        </>
      )}

      {/* Failed state */}
      {status === "failed" && (
        <p
          className="text-sm text-[#9A8F86] italic py-10 text-center"
          style={LORA}
          data-testid="roadmap-failed"
        >
          La feuille de route n'a pas pu être générée pour ce voyage.
        </p>
      )}

      {/* Not available (not paid yet) */}
      {status === "not_available" && (
        <p
          className="text-sm text-[#9A8F86] italic py-10 text-center"
          style={LORA}
          data-testid="roadmap-not-available"
        >
          La feuille de route sera disponible après validation et paiement.
        </p>
      )}

      {/* Completed */}
      {status === "completed" && roadmap?.jours?.length > 0 && (
        <>
          <DaySelector
            jours={roadmap.jours}
            activeIndex={activeDay}
            onSelectDay={scrollToDay}
          />
          <div className="mt-5">
            {roadmap.jours.map((jour, idx) => (
              <DaySection
                key={jour.numero_jour}
                ref={(el) => (dayRefs.current[idx] = el)}
                jour={jour}
                isToday={jour.date === today}
              />
            ))}
          </div>
          {!roadmap.trial_locked && (
            <RoadmapContinue
              tripId={tripId}
              roadmap={roadmap}
              onStarted={() => setRoadmap((r) => ({ ...r, extending: true, has_more: false }))}
            />
          )}
          {roadmap.trial_locked && (
            <LockedDays
              labels={Array.from({ length: Math.max(0, nbJours - roadmap.jours.length) }, (_, i) => `Jour ${roadmap.jours.length + i + 1}`)}
              onUnlock={onUnlock}
            />
          )}
        </>
      )}
    </div>
  );
}
